import type { EquipPicks } from "./types";

import { pipe } from 'fp-ts/function';
import * as TE from 'fp-ts/TaskEither';
import * as R from 'ramda';

import { safeRunSubqlQuery } from "@/utils/requests";
import trimAddress from "@/features/identity/trimAddress";

//
// Types & Interfaces
//

interface MintedOriginShellCounts {
  aggregates: {
    distinctCount: {
      owner: string
    }
  }
}

interface TotalReceivedFoodForShell {
  aggregates: {
    distinctCount: {
      id: string
    }
  }
}

interface FedCountsByNft {
  mintedOriginOfShellNft: {
    fed: {
      aggregates: {
        distinctCount: {
          id: string
        }
      }
    }
  }
}

interface getFedCountsByReceiver {
  aggregates: {
    distinctCount: {
      id: string
    }
  }
}

interface FedRankingItem {
  collectionId: number
  nftId: number
  owner: string
  rarity: string
  race: string
  career: string
  identity: {
    displayName: string
  } | null
  feds: {
    aggregates: {
      distinctCount: {
        id: string
      }
    }
  }
}

interface FedHistoryNode {
  sender: string
  receiver: string
  senderIdentity: {
    id: string
    displayName: string | null
  }
  createdAt: string
  era: number
}

interface FedHistoryRecord {
  sender: {
    id: string
    displayName: string
  }
  isMyself: boolean
  foods: number
  energyPoints: number
  era: number
  createdAt: string
}

//
// Functions
//

export async function getMintedOriginShellCounts(endpoint: string, ownerAddress: string) {
  const taskRunner = pipe(
    safeRunSubqlQuery<{
      mintedOriginOfShellNfts: MintedOriginShellCounts
    }>(
      endpoint,
      `
        query ($ownerAddress: String!) {
          mintedOriginOfShellNfts(
            filter: {
              owner: { equalTo: $ownerAddress }
            }
          ) {
            aggregates {
              distinctCount {
                nftId
              }
            }
          }
        }
      `,
      { ownerAddress }
    ),
    TE.map(
      R.pipe(
        R.pathOr('0', [
          'mintedOriginOfShellNfts',
          'aggregates',
          'distinctCount',
          'nftId',
        ]),
        Number
      )
    ),
  )
  return await taskRunner()
}

export async function getTotalReceivedFoodForShell(endpoint: string, collectionId: number, nftId: number) {
  const taskRunner = pipe(
    safeRunSubqlQuery<{
      fedOriginOfShells: TotalReceivedFoodForShell
    }>(
      endpoint,
      `
        query ($collectionId: Int!, $nftId: Int!) {
          fedOriginOfShells(
            filter: {
              collectionId: {
                equalTo: $collectionId
              },
              nftId: {
                equalTo: $nftId
              }
            }
          ) {
            aggregates {
              distinctCount {
                id
              }
            }
          }
        }
      `,
      { collectionId, nftId }
    ),
    TE.map(
      R.pipe(
        R.pathOr('0', [
          'fedOriginOfShells',
          'aggregates',
          'distinctCount',
          'id',
        ]),
        Number
      )
    ),
  )
  return await taskRunner()
}

export async function getFedCountsByNft(endpoint: string, collectionId: number, nftId: number) {
  const taskRunner = pipe(
    safeRunSubqlQuery<{
      mintedOriginOfShellNft: FedCountsByNft
    }>(
      endpoint,
      `
      query MintedOriginOfShellNft($mintedOriginOfShellNftId: String!) {
        mintedOriginOfShellNft(id: $mintedOriginOfShellNftId) {
          feds {
            aggregates {
              distinctCount {
                id
              }
            }
          }
        }
      }
      `,
      { mintedOriginOfShellNftId: `${collectionId}-${nftId}` }
    ),
    TE.map(
      R.pipe(
        R.pathOr('0', [
          'mintedOriginOfShellNft',
          'feds',
          'aggregates',
          'distinctCount',
          'id'
        ]),
        Number
      )
    ),
  )
  return await taskRunner()
}

export async function getFedCountsByReceiver(endpoint: string, ownerAddress: string) {
  const taskRunner = pipe(
    safeRunSubqlQuery<{
      fedOriginOfShells: getFedCountsByReceiver,
    }>(
      endpoint,
      `
      query DistinctCount($filter: FedOriginOfShellFilter) {
        fedOriginOfShells(filter: $filter) {
          aggregates {
            distinctCount {
              id
            }
          }
        }
      }
      `,
      {
        "filter": {
          "receiver": {
            "equalTo": ownerAddress
          }
        }
      }
    ),
    TE.map(
      R.pipe(
        R.pathOr('0', [
          'fedOriginOfShells',
          'aggregates',
          'distinctCount',
          'id'
        ]),
        Number
      )
    ),
  )
  return await taskRunner()
}

export async function getFullFedRanking(endpoint: string, era?: number | null) {
  const filter = era ? { era: { equalTo: era } } : { era: { notEqualTo: 0 } }
  const taskRunner = pipe(
    safeRunSubqlQuery<{
      mintedOriginOfShellNfts: {
        nodes: FedRankingItem[]
      },
    }>(
      endpoint,
      `
      query MintedOriginOfShellNfts($orderBy: [MintedOriginOfShellNftsOrderBy!], $filter: FedOriginOfShellFilter) {
        mintedOriginOfShellNfts(orderBy: $orderBy) {
          nodes {
            collectionId
            nftId
            owner
            rarity
            race
            career
            identity {
              displayName
            }
            feds(filter: $filter) {
              aggregates {
                distinctCount {
                  id
                }
              }
            }
          }
        }
      }
      `,
      {
        "orderBy": "FEDS_DISTINCT_COUNT_ID_DESC",
        "filter": filter,
      }
    ),
    TE.map(
      R.pipe(
        R.pathOr([], ['mintedOriginOfShellNfts', 'nodes']),
        R.map((item: FedRankingItem) => {
          const { collectionId, nftId, owner, rarity, race, career, identity } = item
          return {
            collectionId,
            nftId,
            owner,
            rarity,
            race,
            career,
            ownerDisplayName: identity ? identity.displayName : null,
            // rank: idx + 1,
            feds: Number(R.pathOr('0', ['feds', 'aggregates', 'distinctCount', 'id'], item)),
          }
        }),
        R.sortBy(i => -R.prop('feds', i)),
        (arr) => R.zip(R.times(R.identity, R.length(arr)), arr),
        R.map(([idx, item]) => ({ ...item, rank: idx + 1 })),
      )
    ),
  )
  return await taskRunner()
}

export async function unlockItem(
  endpoint: string,
  address: string,
  collection_id: number,
  nft_id: number,
  material_id: number,
  points: number,
  msg_signature: string
) {
  // const url = `${endpoint}/unlock_items`
  const url = `/api/synthesis/${collection_id}/${nft_id}/${material_id}/unlock`
  await fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      address, collection_id, nft_id,
      material_id, points, msg_signature
    })
  })
}

export async function saveSynthesisResult(
  endpoint: string,
  address: string,
  collection_id: number,
  nft_id: number,
  size: string,
  compound_data: EquipPicks[],
  background: string,
  msg_signature: string
) {
  // const url = `${endpoint}/api/synthesis/${collection_id}/${nft_id}/compounds`
  const url = `/api/synthesis/${collection_id}/${nft_id}/compounds`
  const ids = R.map(i => i[3].id, compound_data)
  await fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      address, collection_id, nft_id, size,
      compound_data: ids, background,
      msg_signature
    })
  })
}

export async function getFedHistory(endpoint: string, nftId: number, pageSize: number) {
  const taskRunner = pipe(
    safeRunSubqlQuery<{
      fedOriginOfShells: {
        nodes: FedHistoryNode[]
      }
    }>(
      endpoint,
      `
        query Nodes($filter: FedOriginOfShellFilter, $last: Int, $orderBy: [FedOriginOfShellsOrderBy!]) {
          fedOriginOfShells(filter: $filter, last: $last, orderBy: $orderBy) {
            nodes {
              sender
              receiver
              senderIdentity {
                id
                displayName
              }
              createdAt
              era
            }
          }
        }
      `,
      {
        "filter": {
          "nftId": {
            "equalTo": nftId
          }
        },
        "first": pageSize,
        "orderBy": "CREATED_AT_DESC",
      }
    ),
    TE.map(R.pipe(
      R.pathOr([], ['fedOriginOfShells', 'nodes']),
      R.map((node: FedHistoryNode) => {
        return {
          sender: {
            id: node.sender,
            displayName: R.pathOr('', ['senderIdentity', 'displayName'], node) || trimAddress(node.sender),
          },
          isMyself: node.sender === node.receiver,
          era: node.era,
          foods: 1,
          energyPoints: node.sender === node.receiver ? 3 : 2,
          createdAt: node.createdAt,
        } as FedHistoryRecord
      }),
    ))
  )
  return await taskRunner()
}

export async function unlockSecondSuit(
  endpoint: string,
  address: string,
  nft_id: number,
  description: string,
  msg_signature: string
) {
  const url = `${endpoint}/second_suits`
  await fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      address, nft_id, description, msg_signature
    })
  })
}

export async function resetUnlockedItems(
  endpoint: string,
  address: string,
  nft_id: number,
  total: number,
  msg_signature: string
) {
  const url = `${endpoint}/unlock_items/reset`
  await fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ address, nft_id, total, msg_signature })
  })
}